import { unsetCurrentPoiID } from "@/features/cartography/cartographySlice";
import { selectConfigItem } from "@/features/config/configSlice";
import { setSelectedFlight } from "./flightInformationSlice";

import store from "@/app/store";
import { handleSetCurrentPoiID } from "@/features/cartography/actions";
import {
  selectFlightInformationRefreshedAt,
  selectFlightsWithPois,
} from "./flightInformationSlice.selectors";
import type { FlightWithPOI } from "./types";

/**
 * Selects a flight with its POI.
 * @param flightWithPOI - The flight with its POI to select.
 * @param emmitEvent - Whether to emit an event.
 */
export const selectFlight = ({
  flightWithPOI,
  emmitEvent = true,
}: {
  flightWithPOI: FlightWithPOI;
  emmitEvent?: boolean;
}) => {
  const { flight, poiId } = flightWithPOI;

  if (poiId != null) {
    handleSetCurrentPoiID({ emmitEvent, poiId });
  } else {
    store.dispatch(unsetCurrentPoiID());
  }

  store.dispatch(setSelectedFlight(flight.id));
};

/**
 * Selects a flight by id.
 * @param flightId - The id of the flight to select.
 */
export const selectFlightById = (flightId: string) => {
  const flight = findFlightById(flightId);
  if (flight) selectFlight({ flightWithPOI: flight });
};

/**
 * Looks up a flight by id in the current store. Returns null when not found or not yet available.
 * @param flightId - The id of the flight to look up.
 * @returns The flight with its POI or null if not found or not yet available.
 */
export const findFlightById = (
  flightId?: string | null,
): FlightWithPOI | null => {
  if (!flightId?.trim()) {
    return null;
  }

  const state = store.getState();
  if (!selectConfigItem("flight_information.enabled")(state)) {
    return null;
  }

  const flightsRefreshedAt = selectFlightInformationRefreshedAt(state);
  const flightsWithPois = selectFlightsWithPois(state);
  if (flightsRefreshedAt == null || flightsWithPois.length === 0) {
    return null;
  }

  const query = flightId.trim().toLowerCase();
  return (
    flightsWithPois.find((f) => f.flight.id.toLowerCase() === query) ?? null
  );
};
